import {
  Select,
  ListItem,
  Checkbox,
  Typography,
  Box,
  Stack,
} from '@cast/design-system';

import { NodeIndicator } from 'components/indicators';
import { ComputeSpendType } from 'types/cost-report';

import { CostReportChartToggle } from '../../../../cost-report/components/CostReportChartToggle';
import { ComputeSpendMode } from '../../../../cost-report/types/costOverTime';
import { useComputeSpendControls } from '../_hooks';

const optionsMap: Record<ComputeSpendMode, string> = {
  [ComputeSpendMode.DAILY_COST]: 'Daily cost',
  [ComputeSpendMode.CUMULATIVE]: 'Cumulative',
};

type Props = {
  showStorage: boolean;
};

export const ComputeSpendControls = ({ showStorage }: Props) => {
  const { chartNodeType, toggleNodeType, chartMode, changeMode } =
    useComputeSpendControls();

  const options = [
    { label: 'On-demand', type: ComputeSpendType.ON_DEMAND },
    { label: 'Fallback', type: ComputeSpendType.FALLBACK },
    { label: 'Spot', type: ComputeSpendType.SPOT },
  ];

  if (showStorage) {
    options.push({ label: 'Storage', type: ComputeSpendType.STORAGE });
  }

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      py={8}
      px={16}
      data-testid="compute-spend-controls"
    >
      <Box display="flex" gap={48}>
        {options.map(({ label, type }) => (
          <Box
            key={label}
            display="flex"
            alignItems="center"
            component="label"
            gap={8}
          >
            <Checkbox
              size="small"
              checked={chartNodeType[type]}
              onChange={toggleNodeType.bind(null, type)}
              testId={`toggle-${type}`}
            />
            <Typography variant="P12R">{label}</Typography>
            <NodeIndicator size={12} type={type} />
          </Box>
        ))}
      </Box>
      <Stack direction="row" alignItems="center" gap={8}>
        <Select
          testId="change-chart-type"
          size="sm"
          value={chartMode}
          onChange={(value) => changeMode(value)}
          optionLabel={(option) => optionsMap[option]}
          options={[ComputeSpendMode.DAILY_COST, ComputeSpendMode.CUMULATIVE]}
          renderOption={(option, listItemProps) => (
            <ListItem {...listItemProps} component="li">
              <Typography variant="P12R">{optionsMap[option]}</Typography>
            </ListItem>
          )}
        />
        <CostReportChartToggle />
      </Stack>
    </Stack>
  );
};
